'use client'

import * as React from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'

// Teaching layers for grid keyboard browsing (lib/use-grid-keyboard-nav.ts).
// Nothing is shown until a card actually takes focus — a mouse user never sees
// a banner about keys they will not press. Chip styling matches the command
// palette footer so the two features read as one keyboard language.

const KBD = 'rounded bg-muted/60 px-1 py-0.5 font-mono text-[11px] text-muted-foreground'

function Kbd({ children }: { children: React.ReactNode }) {
  return <kbd className={KBD}>{children}</kbd>
}

/** Layer 1 — slim bar, appears on first focus, fades after inactivity. */
export function KeyboardHintBar({
  visible,
  onOpenShortcuts,
}: {
  visible: boolean
  onOpenShortcuts: () => void
}) {
  const { t } = useI18n()
  return (
    <div
      aria-hidden={!visible}
      className={cn(
        'fixed bottom-4 left-1/2 z-40 -translate-x-1/2 transition-all duration-200',
        'flex items-center gap-4 rounded-full border border-border/40 bg-popover/95 px-4 py-2',
        'shadow-lg backdrop-blur-xl text-xs text-muted-foreground',
        'motion-reduce:transition-none',
        visible ? 'opacity-100' : 'pointer-events-none translate-y-2 opacity-0'
      )}
    >
      <span className="flex items-center gap-1.5">
        <Kbd>space</Kbd>
        <Kbd>J</Kbd>
        <Kbd>K</Kbd>
        {t('kbd.nextPrev', 'next / prev')}
      </span>
      <span className="hidden items-center gap-1.5 sm:flex">
        <Kbd>←</Kbd>
        <Kbd>→</Kbd>
        {t('kbd.photos', 'photos')}
      </span>
      <span className="hidden items-center gap-1.5 sm:flex">
        <Kbd>A</Kbd>
        {t('kbd.summary', 'summary')}
      </span>
      <span className="hidden items-center gap-1.5 sm:flex">
        <Kbd>L</Kbd>
        {t('kbd.like', 'like')}
      </span>
      <button
        type="button"
        onClick={onOpenShortcuts}
        className="flex items-center gap-1.5 text-primary transition-colors hover:text-primary/80"
      >
        <kbd className="rounded bg-primary/10 px-1 py-0.5 font-mono text-[11px]">?</kbd>
        {t('kbd.allKeys', 'all keys')}
      </button>
    </div>
  )
}

function Row({ label, keys }: { label: string; keys: string[] }) {
  return (
    <div className="flex items-center justify-between gap-4 py-[3px]">
      <span className="text-muted-foreground">{label}</span>
      <span className="flex shrink-0 items-center gap-1">
        {keys.map((k) => (
          <Kbd key={k}>{k}</Kbd>
        ))}
      </span>
    </div>
  )
}

/** Layer 2 — the `?` sheet. Also reachable from the command palette. */
export function ShortcutsSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n()
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose}>
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('kbd.title', 'Keyboard shortcuts')}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-lg rounded-xl border border-border/40 bg-popover/95 p-5 shadow-lg backdrop-blur-xl"
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <p className="text-[15px] font-medium">{t('kbd.title', 'Keyboard shortcuts')}</p>
              <p className="text-[13px] text-muted-foreground">
                {t('kbd.subtitle', 'Browse results without touching the mouse')}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('palette.close', 'Close')}
              className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid gap-x-8 gap-y-1 text-[13px] sm:grid-cols-2">
            <div>
              <p className="mb-1 text-[12px] text-muted-foreground/70">{t('kbd.move', 'Move')}</p>
              <Row label={t('kbd.startBrowsing', 'Start browsing')} keys={['J']} />
              <Row label={t('kbd.nextListing', 'Next listing')} keys={['space']} />
              <Row label={t('kbd.nextPrevLong', 'Next / previous')} keys={['J', 'K']} />
              <Row label={t('kbd.photosLong', 'Photos')} keys={['←', '→']} />
              <Row label={t('kbd.stop', 'Stop browsing')} keys={['esc']} />
            </div>
            <div>
              <p className="mb-1 text-[12px] text-muted-foreground/70">
                {t('kbd.actOn', 'Act on the focused card')}
              </p>
              <Row label={t('kbd.summaryLong', 'AI summary')} keys={['A']} />
              <Row label={t('kbd.likeLong', 'Like')} keys={['L']} />
              <Row label={t('kbd.hideLong', 'Hide')} keys={['X']} />
              <Row label={t('kbd.open', 'Open details')} keys={['↵']} />
              <Row label={t('kbd.openNewTab', 'Open in a new tab')} keys={['⌘', '↵']} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
